import moment from "moment";

/**
 * Formats the lead creation source name for display
 * @param name - The lead creation source name
 * @returns Formatted lead creation source name
 */
export const formatLeadCreationName = (name: string): string => {
  switch (name) {
    case "self_sourced":
      return "Self Sourced";
    case "CSV":
      return "CSV Upload";
    case "as_admin":
      return "Admin Upload";
    case "hubspot":
      return "Hubspot";
    case "salesforce":
      return "Salesforce";
    default:
      return name;
  }
};

/**
 * Formats filter pills based on the filter type and value
 * @param key - The filter key
 * @param value - The filter value
 * @returns Array of formatted filter values
 */
export const formatFilterPills = (key: string, value: unknown): string[] => {
  if (!value) return [];

  switch (key) {
    case "next_scheduled_event":
      return (value as { next_scheduled_event_types: string[] })?.next_scheduled_event_types || [];
    case "sequences":
      return Array.isArray(value) ? (value as Array<{ label: string }>).map(({ label }) => label) : [];
    case "brands":
      return Array.isArray(value) ? (value as Array<{ label: string }>).map(({ label }) => label) : [];
    case "custom_fields":
      return Array.isArray(value) ? (value as Array<{ key: string }>).map(({ key }) => key) : [];
    default:
      return Array.isArray(value) ? (value as string[]) : [];
  }
};

/**
 * Converts table view name to analytics name
 * @param tableView - The table view name
 * @returns Analytics name
 */
export const tabToAnalytics = (tableView: string): string => {
  switch (tableView) {
    case "Transfer":
    case "TransferMetrics":
      return "Transfer Metrics";
    case "TransferQuality":
      return "Transfer Quality";
    default:
      return tableView;
  }
};

/**
 * Generates date range options for the dashboard
 * @returns Array of date range options
 */
export const generateDateRangeOptions = () => [
  { start: moment().startOf("day").format(), end: moment().endOf("day").format(), label: "Today", value: "Today" },
  {
    start: moment().subtract(24, "hours").startOf("day").format(),
    end: moment().subtract(24, "hours").endOf("day").format(),
    label: "Yesterday",
    value: "Yesterday",
  },
  {
    start: moment().startOf("week").format(),
    end: moment().endOf("week").format(),
    label: "This Week",
    value: "ThisWeek",
  },
  {
    start: moment().subtract(1, "week").startOf("week").format(),
    end: moment().subtract(1, "week").endOf("week").format(),
    label: "Last Week",
    value: "LastWeek",
  },
  {
    start: moment().startOf("month").format(),
    end: moment().endOf("day").format(),
    label: "Month to Date",
    value: "MonthToDate",
  },
  {
    start: moment().subtract(1, "months").startOf("month").format(),
    end: moment().subtract(1, "months").endOf("month").format(),
    label: "Last Month",
    value: "LastMonth",
  },
  {
    start: moment().startOf("year").format(),
    end: moment().endOf("day").format(),
    label: "Year to Date",
    value: "YearToDate",
  },
  {
    start: moment().subtract(1, "years").startOf("year").format(),
    end: moment().subtract(1, "years").endOf("year").format(),
    label: "Last Year",
    value: "LastYear",
  },
  {
    start: moment().subtract(1, "months").startOf("day").format(),
    end: moment().endOf("day").format(),
    label: "Custom Range",
    value: "CustomRange",
  },
];
